import { FunctionComponent, useCallback } from "react";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import { Text } from "@ui-kit/Text/Text";
import { useTranslation } from "react-i18next";
import { OnboardingStepProps } from "../OnboardingScreens/OnboardingStepProps";
import { TextInput } from "@ui-kit/TextInput/TextInput";
import { Form } from "@ui-kit/Form/Form";
import {
  getPlan,
  useCreatePlanMutation,
  useUpdatePlanMutation,
} from "@store/services/plan";
import { FormField } from "@ui-kit/Form/FormField";
import { MapIcon } from "@icons/MapIcon";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { ExternalIcon } from "@icons/ExternalIcon";
import { PlanItem } from "../../entities/PlanItemList/PlanItem";
import { PlanItem as PlanItemDto } from "@dto/PlanItem";
import { Place } from "@dto/Place";
import { useAppDispatch, useAppSelector } from "@store/store";
import { selectPlan } from "@store/plan/planReducer";

interface CreatePlanForm {
  name: string;
}

const CreatePlan: FunctionComponent<
  OnboardingStepProps & { entity?: Place }
> = ({ onNextPress, entity, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<CreatePlanForm>();
  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();
  const isLoading = isCreating || isUpdating;
  const plan = useAppSelector(selectPlan);
  const dispatch = useAppDispatch();

  const handleLaterPress = useCallback(() => {
    if (!entity) {
      onClose();
    } else {
      onNextPress("featuredItem");
    }
  }, [entity, onClose, onNextPress]);

  const handleSubmit = useCallback(
    async (values: CreatePlanForm) => {
      if (!plan?.id) {
        const planCreate = await createPlan({ name: values.name });
        // Must get plan so invalidating rtk query tags re-fetches plan.
        await dispatch(getPlan.initiate(planCreate.data?.id ?? -1));
      } else {
        await updatePlan({ id: plan.id, name: values.name });
      }
      if (!entity) {
        onClose();
      } else {
        onNextPress("featuredItem");
      }
    },
    [createPlan, dispatch, entity, onClose, onNextPress, plan?.id, updatePlan],
  );

  const handleCreatePlanPress = useCallback(() => {
    form.submit();
  }, [form]);

  return (
    <Form
      form={form}
      name="onboardingCreatePlan"
      className="content-container"
      disabled={isLoading}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{ name: plan?.name ?? "" }}>
      <div className="title-description">
        <Text variant="fontHeadingXlarge">
          {t("screens.onboarding.createPlanTitle")}
        </Text>
        <Text variant="fontBodyMedium">
          {t("screens.onboarding.createPlanDescription")}
        </Text>
      </div>
      <div>
        <FormField
          label={t("screens.onboarding.nameLabel")}
          name="name"
          noMargin={true}>
          <TextInput
            name="name"
            size="large"
            placeholder={t("screens.onboarding.namePlaceholder")}
            autoFocus={true}
          />
        </FormField>
      </div>
      <div className="actions-horizontal">
        <TabButton
          variant="primary"
          flex={true}
          wide={true}
          label={t("screens.onboarding.createPlanButton")}
          onPress={handleCreatePlanPress}
          disabled={isLoading}
        />
        <TabButton
          variant="inverse"
          flex={true}
          active={true}
          wide={true}
          label={t("screens.onboarding.laterButton")}
          onPress={handleLaterPress}
          disabled={isLoading}
        />
      </div>
    </Form>
  );
};

const CreatePlanBackground: FunctionComponent<{ entity?: Place }> = ({
  entity,
}) => {
  const { t } = useTranslation();

  return (
    <div className="onboarding-background-color">
      <div className="plan-mock">
        <img
          src="/images/onboarding/onboarding-cover-photo.png"
          alt="Create Plan"
        />
        <div className="plan-name-mock">
          <Text variant="fontHeadingMedium">
            {t("screens.onboarding.mockPlanName")}
          </Text>
        </div>
        <div className="empty-plan-component">
          <Text variant="fontHeadingSmall" color="neutral700">
            {t("screens.plan.noPlanItems.title")}
          </Text>
          <Text variant="fontBodySmall" color="neutral700">
            {t("screens.plan.noPlanItems.description")}
          </Text>
          <div className="empty-plan-button">
            <MapIcon size={18} color={colors.primary60} />
            {t("screens.plan.noPlanItems.exploreLabel")}
          </div>
        </div>
        <div className="start-point-mock">
          <ExternalIcon size={18} color={colors.primary60} svgName="location" />
          <Text variant="fontBodyMedium" color={colors.primary60}>
            {t("screens.plan.addStartPoint")}
          </Text>
        </div>
        <div className="plan-item-mock">
          {entity && (
            <PlanItem
              planItem={
                {
                  plannable: entity,
                } as unknown as PlanItemDto
              }
              ordinal={1}
              actionsEnabled={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { CreatePlan, CreatePlanBackground };
